import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  uploadShopifyInventory,
  clearUploadResult
} from '../../store/slices/inventorySlice';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';

const StyleUpload: React.FC = () => {
  const dispatch = useAppDispatch();
  const { uploadLoading, uploadError, uploadResult } = useAppSelector((state) => state.inventory);
  const { user } = useAppSelector((state) => state.auth);

  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (fileExtension !== '.csv') {
      alert('Invalid file type. Please upload .csv files only.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    try {
      await dispatch(uploadShopifyInventory(file)).unwrap();
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  // Only managers can upload styles
  if (user?.role !== 'manager') {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-gray-600">You don't have permission to upload style catalogs.</p>
        <p className="text-sm text-gray-500 mt-2">Style catalog uploads are restricted to Store Managers only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-blue-500 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Shopify Style Catalog Upload</h3>
            <div className="text-sm text-blue-700 mt-1 space-y-1">
              <p>• Export your styles catalog from Shopify in CSV format</p>
              <p>• Ensure columns include: Handle, Title, SKU, Option1 Name, Option1 Value, Option2 Name, Option2 Value</p>
              <p>• <strong>Creates styles and variants only - no inventory quantities</strong></p>
              <p>• Use "Stock Entry" or "Inventory Upload" afterwards to add actual stock quantities</p>
              <p>• Gift vouchers and gift cards will be automatically skipped</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : uploadLoading
            ? 'border-gray-300 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {uploadLoading ? (
          <div className="space-y-4">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600" />
            <div>
              <p className="text-lg font-medium text-gray-900">Uploading Style Catalog...</p>
              <p className="text-sm text-gray-600">Please wait while we process your Shopify export</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                Drop your Shopify styles catalog CSV here
              </p>
              <p className="text-sm text-gray-600">or click to browse</p>
            </div>
            
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="style-file-upload"
              disabled={uploadLoading}
            />
            
            <label
              htmlFor="style-file-upload"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 cursor-pointer disabled:opacity-50"
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose CSV File
            </label>
            
            <p className="text-xs text-gray-500">
              Maximum file size: 10MB. Only CSV format accepted.
            </p>
          </div>
        )}
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div className={`rounded-lg p-4 ${uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-start">
            {uploadResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
            )}
            
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {uploadResult.success ? 'Style Catalog Upload Successful' : 'Upload Failed'}
              </h3>
              
              <p className={`text-sm mt-1 ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {uploadResult.message}
              </p>
              
              {uploadResult.success && (
                <div className="mt-3 space-y-1 text-sm text-green-700">
                  <p>• Processed records: {uploadResult.processed_records}</p>
                  {uploadResult.new_styles && <p>• New styles created: {uploadResult.new_styles}</p>}
                  {uploadResult.existing_styles && <p>• Existing styles: {uploadResult.existing_styles}</p>}
                  {uploadResult.new_variants && <p>• New variants created: {uploadResult.new_variants}</p>}
                  {uploadResult.skipped_gift_vouchers && <p>• Skipped gift vouchers: {uploadResult.skipped_gift_vouchers}</p>}
                </div>
              )}
              
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-red-800">Errors:</h4>
                  <div className="mt-1 max-h-32 overflow-y-auto">
                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                      <p key={index} className="text-xs text-red-700">
                        Row {error.row}: {error.error}
                      </p>
                    ))}
                    {uploadResult.errors.length > 5 && (
                      <p className="text-xs text-red-700">
                        ... and {uploadResult.errors.length - 5} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={() => dispatch(clearUploadResult())}
              className="ml-3 text-gray-400 hover:text-gray-500"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Upload Error</h3>
              <p className="text-sm text-red-700 mt-1">{uploadError}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StyleUpload;
